import React, { useEffect } from "react";
import "./Homepage.css";
import { Button, Stack, Typography, Box } from "@mui/joy";
import HowTo from "./HowTo";
import Stats from "./Stats";
import FrequentlyAskedQuestions from "./FrequentlyAskedQuestions";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import SEOHead from "../../components/SEOHead/SEOHead";
import seoConfig from "../../utils/seoConfig";
import CongressImg from "../../congress-hero.png";

function Homepage() {
  useEffect(() => {
    document.title = seoConfig.pages.home.title;
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, "", path);
    const navEvent = new PopStateEvent("popstate");
    window.dispatchEvent(navEvent);
  };

  const goToBills = (e) => { e.preventDefault(); navigate("/bills"); };
  const goToLegislators = (e) => { e.preventDefault(); navigate("/legislators"); };
  const goToResources = (e) => { e.preventDefault(); navigate("/resources"); };

  return (
    <>
      <SEOHead
        title={seoConfig.pages.home.title}
        description={seoConfig.pages.home.description}
        keywords={seoConfig.pages.home.keywords}
        path={seoConfig.pages.home.path}
        structuredData={[
          seoConfig.organization,
          seoConfig.getWebsiteSchema(),
          seoConfig.getGovernmentServiceSchema(),
          seoConfig.getBreadcrumbs("/", "Home"),
        ]}
      />

      {/* Entire page is dark #003049 */}
      <Box sx={{ width: "100%", backgroundColor: "#003049" }}>

        {/* Hero */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            minHeight: { xs: "480px", md: "600px" },
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* Congress illustration — blended into dark bg */}
          <Box
            component="img"
            src={CongressImg}
            alt=""
            aria-hidden="true"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              right: 0,
              top: 0,
              width: "50%",
              maxWidth: "1640px",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: "1200px",
              width: "100%",
              mx: "auto",
              px: { xs: "1.25rem", md: "2rem" },
              py: { xs: "5rem", md: "6rem" },
            }}
          >
            <Stack spacing={2} sx={{ mb: "2rem" }}>
              <Typography
                component="p"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "var(--ce-dark-eyebrow)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Legislation Tracker
              </Typography>
              <Typography
                level="h1"
                sx={{
                  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
                  fontWeight: 700,
                  color: "white",
                  lineHeight: 1.2,
                  maxWidth: { md: "480px" },
                }}
              >
                Make your voice heard
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  color: "white",
                  lineHeight: 1.65,
                  maxWidth: { md: "480px" },
                }}
              >
                Browse active legislation in plain language and contact your
                representative directly. 500+ federal bills tracked, updated daily.
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                size="lg"
                onClick={goToBills}
                sx={{
                  backgroundColor: "var(--ce-accent)",
                  color: "white",
                  px: 3,
                  py: 1.5,
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "6px",
                  minHeight: "48px",
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: "var(--ce-accent-hover)" },
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                Browse legislation
              </Button>
              <Button
                size="lg"
                variant="outlined"
                onClick={goToLegislators}
                endDecorator={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "6px",
                  minHeight: "48px",
                  borderColor: "var(--ce-accent)",
                  color: "var(--ce-accent)",
                  backgroundColor: "white",
                  transition: "background-color 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: "#f0f8ff",
                    borderColor: "var(--ce-accent-hover)",
                  },
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                View the current congress members
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* HowTo */}
        <HowTo />

        {/* Stats */}
        <Stats />

        {/* FAQ */}
        <FrequentlyAskedQuestions goToResources={goToResources} />

      </Box>
    </>
  );
}

export default Homepage;
